import { useTranslation } from "translation/useTranslation";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ListPagination from "components/ui/ListPagination";
import { Field, Input, InputAffix, Select } from "components/ui";
import "react-tooltip/dist/react-tooltip.css";

export default function ScorecardInstanceEditor({
  rows,
  options,
  intro,
  fieldLabel,
  contextKey,
  contextLabel,
  values,
  onValueChange,
  selected,
  setSelected,
  onSelectionChange,
  search,
  onSearchChange,
  total,
  loadingPool,
  page,
  perPage,
  totalPages,
  totalRows,
  setPage,
  setPerPage,
}) {
  const { translate, editableTranslate } = useTranslation();

  const selectOptions = (options || []).map(o => ({ value: o.hash_id, label: o.name }));

  const handleToggle = (hashId, checked) => {
    setSelected(prev => {
      if (checked) {
        return prev.includes(hashId) ? prev : [...prev, hashId];
      }
      return prev.filter(id => id !== hashId);
    });
    if (onSelectionChange) {
      const row = rows.find(r => r.hash_id === hashId) || { hash_id: hashId };
      onSelectionChange([row], checked);
    }
  };

  return (
    <div data-testid="scorecard-instance-editor">
      <div className="mb-3">{intro}</div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
          <Input
            type="text"
            placeholder={translate('global.search', 'Suche')}
            maxLength={255}
            value={search}
            data-testid="scorecard-search"
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          />
        </InputAffix>
        <div className="text-muted text-nowrap ml-2">{selected.length} / {total}</div>
      </div>

      {
        loadingPool && (
          <div className="text-center p-2" data-testid="scorecard-loading">
            <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
          </div>
        )
      }

      {
        rows.length === 0
          ? (!loadingPool && <div className="text-muted p-2" data-testid="scorecard-empty">{editableTranslate('global.no_data', 'Keine Daten')}</div>)
          : rows.map(row => {
            const isChecked = selected.includes(row.hash_id);
            return (
              <div key={row.hash_id} className="border-bottom py-2" data-testid={`scorecard-row-${row.hash_id}`}>
                <div className="form-check">
                  {row.tooltip && <ReactTooltip id={`scorecard-row-tooltip-${row.hash_id}`} content={row.tooltip} place="right" />}
                  <label className="form-check-label">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => handleToggle(row.hash_id, e.target.checked)}
                      data-testid={`scorecard-row-checkbox-${row.hash_id}`}
                    />
                    <span className="ml-1" data-tooltip-id={row.tooltip ? `scorecard-row-tooltip-${row.hash_id}` : undefined}>{row.label}</span>
                  </label>
                </div>
                {
                  isChecked && (
                    <div className="p-3 bg-light rounded mb-2">
                      <div className="row">
                        <div className="col-xl-4 col-lg-6 form-group mb-0">
                          <Field label={fieldLabel}>
                            <div style={{ display: 'contents' }} data-testid={`scorecard-value-${row.hash_id}`}>
                              <Select
                                options={selectOptions}
                                value={selectOptions.find(o => o.value === values[row.hash_id]) || null}
                                onChange={option => onValueChange(row.hash_id, option ? option.value : '')}
                                isClearable
                                placeholder={translate('form.select_placeholder')}
                              />
                            </div>
                          </Field>
                        </div>
                        {
                          contextKey && <div className="col-xl-4 col-lg-6 form-group mb-0">
                            <Field label={contextLabel} htmlFor={`scorecard-context-${row.hash_id}`}>
                              <Input
                                id={`scorecard-context-${row.hash_id}`}
                                value={row[contextKey] || '-'}
                                data-testid={`scorecard-context-${row.hash_id}`}
                                disabled={true}
                                readOnly={true}
                              />
                            </Field>
                          </div>
                        }
                      </div>
                    </div>
                  )
                }
              </div>
            );
          })
      }

      <ListPagination
        page={page}
        perPage={perPage}
        totalPages={totalPages}
        totalRows={totalRows}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </div>
  );
}
